import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, X, Image as ImageIcon, Upload, Link as LinkIcon, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';

const empty = { title: '', subtitle: '', cta: '', href: '', image: '', order: 0, active: true, tag: '' };

// Compress large images client-side to a max 1600px width JPEG @ 0.82 quality
const readAndCompressImage = (file, maxW = 1600, quality = 0.82) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(dataUrl);
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const BannersSection = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modal, setModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get('/admin/banners').then((r) => setBanners(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...empty, order: (banners.length || 0) + 1 }); setModal(true); };
  const openEdit = (b) => { setEditing({ ...b }); setModal(true); };

  const toggle = async (b) => {
    await api.patch(`/admin/banners/${b.id}`, { ...b, active: !b.active });
    toast({ title: !b.active ? 'Banner activado' : 'Banner desactivado' });
    load();
  };

  const move = async (b, delta) => {
    await api.patch(`/admin/banners/${b.id}`, { ...b, order: (b.order || 0) + delta });
    load();
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast({ title: 'Solo se permiten imágenes' }); return; }
    if (file.size > 15 * 1024 * 1024) { toast({ title: 'Imagen muy grande', description: 'Máximo 15 MB' }); return; }
    setUploading(true);
    try {
      const dataUrl = await readAndCompressImage(file);
      setEditing((prev) => ({ ...prev, image: dataUrl }));
      toast({ title: 'Imagen cargada', description: 'Recuerda guardar para aplicar los cambios.' });
    } catch (e) {
      toast({ title: 'Error al procesar imagen' });
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer?.files?.[0]);
  };

  const save = async () => {
    const payload = {
      title: editing.title || '',
      subtitle: editing.subtitle || '',
      cta: editing.cta || '',
      href: editing.href || '',
      image: editing.image || '',
      order: Number(editing.order) || 0,
      active: !!editing.active,
      tag: editing.tag || '',
    };
    try {
      if (editing.id) {
        await api.patch(`/admin/banners/${editing.id}`, payload);
        toast({ title: 'Banner actualizado' });
      } else {
        await api.post('/admin/banners', payload);
        toast({ title: 'Banner creado' });
      }
      setModal(false);
      load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'No se guardó' }); }
  };

  const remove = async (b) => {
    if (!window.confirm(`¿Eliminar el banner "${b.title}"?`)) return;
    await api.delete(`/admin/banners/${b.id}`);
    toast({ title: 'Banner eliminado' });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white">Banners del Home</h1>
          <p className="text-white/50 text-sm mt-1">Configura las imágenes y textos del carrusel principal.</p>
        </div>
        <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-black font-bold shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Nuevo banner
        </Button>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-orange-500" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {banners.map((b) => (
            <div key={b.id} className="bg-neutral-950 border border-white/10 rounded-2xl overflow-hidden">
              <div className="relative h-40 bg-neutral-900">
                {b.image ? (
                  <img src={b.image} alt={b.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30"><ImageIcon className="w-8 h-8" /></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
                <div className="absolute inset-0 p-4 flex flex-col justify-end">
                  <h3 className="text-white font-black text-lg drop-shadow">{b.title}</h3>
                  <p className="text-white/80 text-xs line-clamp-1">{b.subtitle}</p>
                </div>
                <span className={`absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full ${b.active ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-white/10 text-white/60 border border-white/20'}`}>
                  {b.active ? 'Activo' : 'Desactivado'}
                </span>
              </div>
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="text-xs text-white/60">Orden: <span className="text-white font-semibold">{b.order}</span> · CTA: <span className="text-white font-semibold">{b.cta}</span></div>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(b, -1)} className="w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center" title="Subir"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => move(b, 1)} className="w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center" title="Bajar"><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => toggle(b)} className="w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center" title={b.active ? 'Ocultar' : 'Mostrar'}>
                    {b.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => openEdit(b)} className="w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(b)} className="w-8 h-8 rounded-md bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 inline-flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
          {banners.length === 0 && (
            <div className="col-span-full text-center py-16 bg-neutral-950 border border-white/10 rounded-2xl text-white/50">
              Sin banners aún. Los banners por defecto se mostrarán en el sitio hasta que crees uno.
            </div>
          )}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="sm:max-w-2xl bg-neutral-950 border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editing?.id ? 'Editar banner' : 'Nuevo banner'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-white/60">Título</label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="Vapea con estilo" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/60">Subtítulo</label>
                <Textarea value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} rows={2} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-xs text-white/60">Texto del botón (CTA)</label>
                <Input value={editing.cta} onChange={(e) => setEditing({ ...editing, cta: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-xs text-white/60">Enlace del botón</label>
                <Input value={editing.href} onChange={(e) => setEditing({ ...editing, href: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="/catalogo?cat=e-juice" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/60">Etiqueta / Badge (pequeño texto arriba del título)</label>
                <Input value={editing.tag || ''} onChange={(e) => setEditing({ ...editing, tag: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="Ej. Vape Shop premium 18+" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/60 mb-1.5 block font-semibold uppercase tracking-wider">Imagen del banner</label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileRef.current?.click()}
                  data-testid="banner-image-dropzone"
                  className={`relative cursor-pointer rounded-xl overflow-hidden border-2 border-dashed transition-all ${
                    dragOver ? 'border-orange-500 bg-orange-500/10' : 'border-white/15 bg-white/5 hover:border-orange-500/60 hover:bg-white/[0.07]'
                  }`}
                  style={{ minHeight: 180 }}
                >
                  {editing.image ? (
                    <>
                      <img src={editing.image} alt="preview" className="w-full h-48 object-cover object-center" onError={(e) => { e.target.style.display = 'none'; }} />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-wider font-black text-white bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">
                          Imagen cargada
                        </span>
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                            className="text-[10px] uppercase tracking-wider font-black text-black bg-white hover:bg-orange-500 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                          >
                            <Upload className="w-3 h-3" /> Reemplazar
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setEditing({ ...editing, image: '' }); }}
                            className="text-[10px] uppercase tracking-wider font-black text-white bg-rose-500 hover:bg-rose-600 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Quitar
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                      {uploading ? (
                        <>
                          <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
                          <p className="text-sm text-white/70 mt-2">Procesando imagen...</p>
                        </>
                      ) : (
                        <>
                          <div className="w-12 h-12 rounded-full bg-orange-500/15 border border-orange-500/30 flex items-center justify-center">
                            <Upload className="w-5 h-5 text-orange-400" />
                          </div>
                          <p className="text-white font-semibold mt-3">Arrastra una imagen aquí</p>
                          <p className="text-white/50 text-xs mt-1">o haz clic para elegir un archivo</p>
                          <p className="text-white/30 text-[10px] mt-3 uppercase tracking-wider font-bold">JPG · PNG · WEBP · Máx 15 MB</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="banner-file-input"
                />
                <details className="mt-3 group">
                  <summary className="text-xs text-white/50 hover:text-white cursor-pointer inline-flex items-center gap-1.5 select-none">
                    <LinkIcon className="w-3 h-3" /> O usar URL externa
                  </summary>
                  <Input
                    value={editing.image.startsWith('data:') ? '' : editing.image}
                    onChange={(e) => setEditing({ ...editing, image: e.target.value })}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="https://ejemplo.com/imagen.jpg"
                  />
                </details>
              </div>
              <div>
                <label className="text-xs text-white/60">Orden</label>
                <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Activo
                </label>
              </div>
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setModal(false)} className="bg-transparent border-white/20 text-white hover:bg-white/10"><X className="w-4 h-4 mr-1" /> Cancelar</Button>
                <Button onClick={save} className="bg-orange-500 hover:bg-orange-600 text-black font-bold">Guardar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannersSection;
